/* eslint-disable react-hooks/exhaustive-deps */
import { Table, Space, Button } from "antd";
import axios from "axios";
import dayjs from "dayjs";
import { useEffect, useState } from "react";

import CreateCampaignModal from "./CreateCampaignModal";
import CampaignTriggersModal from "./TriggersModal";
import ViewDetailsModal from "./ViewDetailsModal";
import { showErrorNotification } from "../../../utils/notification";
import { clamp } from "../../../utils/responsiveHelpers";

const BulkNotificationsCampaignsDashboard = ({ userConfig, ROOT_URL }) => {
  const TARGET_URL = "admin/bulk-notifications/campaign";

  const [campaignsList, setCampaignsList] = useState([]);
  const [creationModalOpen, setCreationModalOpen] = useState(false);

  const [isViewContentModalOpen, setIsViewContentModalOpen] = useState(false);
  const [currentCampaignDetails, setCurrentCampaignDetails] = useState({});

  const [currentCampaignId, setCurrentCampaignId] = useState();
  const [isTriggerCreationModalOpen, setIsTriggerCreationModalOpen] = useState(false);

  const [senderEmailOptions, setSenderEmailOptions] = useState([]);
  const [personalizationVariables, setPersonalizationVariables] = useState([]);

  const fetchCampaignsList = () => {
    axios
      .get(`${ROOT_URL}/${TARGET_URL}/list`, {})
      .then((res) => {
        const { campaigns } = res.data;
        setCampaignsList(campaigns);
      })
      .catch((err) => {
        showErrorNotification(err);
      });
  };

  const handleCreateCampaign = (inputParams) => {
    axios
      .post(`${ROOT_URL}/${TARGET_URL}/create`, {
        inputParams
      })
      .then(() => {
        setCreationModalOpen(false);
        fetchCampaignsList();
      })
      .catch((err) => {
        showErrorNotification(err);
      });
  };

  const handleSendTestEmails = (inputParams) => {
    axios
      .post(`${ROOT_URL}/${TARGET_URL}/dry-run`, {
        inputParams
      })
      .then(() => {})
      .catch((err) => {
        showErrorNotification(err);
      });
  };

  const fetchCampaignDetails = (campaignId) => {
    axios
      .get(`${ROOT_URL}/${TARGET_URL}/details`, {
        params: {
          campaignId
        }
      })
      .then((res) => {
        const { campaignDetails } = res.data;
        setCurrentCampaignDetails(campaignDetails);
        setIsViewContentModalOpen(true);
      })
      .catch((err) => {
        showErrorNotification(err);
      });
  };

  const archiveCampaign = (campaignId) => {
    axios
      .post(`${ROOT_URL}/${TARGET_URL}/archive`, {
        campaignId
      })
      .then(() => {
        fetchCampaignsList();
      })
      .catch((err) => {
        showErrorNotification(err);
      });
  };

  const fetchCampaignFormOptions = () => {
    axios
      .get(`${ROOT_URL}/${TARGET_URL}/form-options`, {})
      .then((res) => {
        const { senderEmails, personalizationVariables: variables } = res.data;
        setSenderEmailOptions(senderEmails);
        setPersonalizationVariables(variables);
      })
      .catch((err) => {
        showErrorNotification(err);
      });
  };

  const campaignsListColumns = [
    {
      title: "S No.",
      dataIndex: "index",
      key: "index"
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => {
        if (typeof a.name === "string" && typeof b.name === "string") {
          return a.name?.localeCompare(b.name) | 0;
        }
        // Fallback comparison if not string (handle the case if one or both values are not strings)
        return 0;
      }
    },
    {
      title: "Test Emails",
      dataIndex: "testEmails",
      key: "testEmails"
    },
    {
      title: "Created By",
      dataIndex: "createdBy",
      key: "createdBy"
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (createdAt) => dayjs(createdAt).format("DD MMM YYYY | HH:mm")
    },
    {
      title: "Actions",
      key: "action",
      render: (row) => (
        <div
          id="bulk_notification_campaign_actions"
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-around"
          }}
        >
          <Space wrap>
            <>
              <Button
                type="primary"
                onClick={() => {
                  setCurrentCampaignId(row.key);
                  fetchCampaignDetails(row.key);
                }}
              >
                View Details
              </Button>
              {userConfig?.bulkNotifications?.hasCampaignTriggerAccess && (
                <Button
                  type="primary"
                  onClick={() => {
                    setCurrentCampaignId(row.key);
                    setIsTriggerCreationModalOpen(true);
                  }}
                >
                  View Triggers
                </Button>
              )}
              <Button
                type="primary"
                danger
                onClick={() => {
                  archiveCampaign(row.key);
                }}
              >
                Archive
              </Button>
            </>
          </Space>
        </div>
      )
    }
  ];

  useEffect(() => {
    fetchCampaignsList();
    fetchCampaignFormOptions();
  }, []);

  return (
    <div
      style={{
        marginTop: "20px",
        marginLeft: clamp(12, 30),
        marginRight: clamp(12, 30)
      }}
    >
      <div
        style={{
          marginBottom: "10px"
        }}
      >
        {userConfig?.bulkNotifications?.hasCampaignCreationAccess && (
          <Button
            type="primary"
            onClick={() => {
              setCreationModalOpen(true);
            }}
          >
            Create Campaign
          </Button>
        )}
      </div>
      <div
        style={{
          marginBottom: "10px",
          display: "flex",
          flexDirection: "column",
          gap: "5px"
        }}
      >
        <h3>Campaigns</h3>
        <Table
          columns={campaignsListColumns}
          dataSource={campaignsList}
          bordered
          pagination={false}
          scroll={{ x: "max-content" }}
        />
      </div>
      {creationModalOpen && (
        <CreateCampaignModal
          senderEmailOptions={senderEmailOptions}
          personalizationVariables={personalizationVariables}
          isModalOpen={creationModalOpen}
          setIsModalOpen={setCreationModalOpen}
          handleSendTestEmails={handleSendTestEmails}
          handleCampaignCreation={handleCreateCampaign}
        />
      )}
      {isViewContentModalOpen && (
        <ViewDetailsModal
          campaignDetails={currentCampaignDetails}
          isModalOpen={isViewContentModalOpen}
          setIsModalOpen={setIsViewContentModalOpen}
        />
      )}
      {isTriggerCreationModalOpen && (
        <CampaignTriggersModal
          campaignId={currentCampaignId}
          ROOT_URL={ROOT_URL}
          isModalOpen={isTriggerCreationModalOpen}
          setIsModalOpen={setIsTriggerCreationModalOpen}
          senderEmailOptions={senderEmailOptions}
        />
      )}
    </div>
  );
};

export default BulkNotificationsCampaignsDashboard;
